import type { IProfileData } from "@/types";
import { isNowBasedDateName } from "@features/dynamic-variables/date-variable/date-resolver";
import { accountVariables } from "../data";

interface IFsValues {
  [key: string]: unknown;
}

const ORGANIZATION_VARIABLES = accountVariables.map((v) => v.variable.name);

export const getDefaultValues = (fsVars: any, profileData: IProfileData) => {
  return fsVars.reduce((acc: IFsValues, current: any) => {
    if (ORGANIZATION_VARIABLES.includes(current.name)) {
      // Account variables use underscore-separated names like
      // "sender_email" → profileData.sender.email or
      // "organization_logo" → profileData.org.logo. Normalize the
      // "organization_" prefix to "org_" before swapping underscores for
      // dots so the path matches profileData's `org` key (not
      // "organization").
      const path = current.name
        .replace(/^organization_/, "org_")
        .split("_")
        .join(".");
      const value = get(profileData, path) || "";
      acc[current.name] = value;
    } else if (current.name.startsWith("sender_")) {
      // A profile custom field (`sender_<slug>`, see create-variable-dialog):
      // the sender's own value from My profile, else the field's default.
      const value = profileData.sender.customFields?.[current.name];
      acc[current.name] = value ? value : current.default;
    } else if (isNowBasedDateName(current.name) && !current.default) {
      // Built-in current-date variables start on today's date (the share
      // dialog shows it in a calendar) instead of an empty text default.
      acc[current.name] = todayDateValue();
    } else {
      acc[current.name] = current.default;
    }

    return acc;
  }, {});
};

const get = (obj: any, path: string) => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
};

/** Local calendar date as `YYYY-MM-DD` — the share dialog's date value format. */
const todayDateValue = (): string => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};
